import { useState, useCallback, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api as Api } from 'api/http';
import { CompanyContext } from 'contexts/CompanyContext';
import axios from 'axios';
import { proxyUrl } from 'utils/proxy';

/**
 * Custom hook for managing voices from ElevenLabs and company voices
 * Provides CRUD operations, fetching, and filtering for voice library
 */
const useVoices = () => {
    const { company } = useContext(CompanyContext);
    const qc = useQueryClient();

    // State management
    const [voices, setVoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [meta, setMeta] = useState(null);
    const [links, setLinks] = useState(null);
    const [operationLoading, setOperationLoading] = useState({});
    
    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Set loading state for specific operations
     */
    const setOperationLoadingState = useCallback((operation, isLoading) => {
        setOperationLoading(prev => ({
            ...prev,
            [operation]: isLoading
        }));
    }, []);

    /**
     * Fetch company voices with filtering and pagination support
     * @param {Object} options - Fetch options
     * @param {number} options.page - Page number (1-based)
     * @param {number} options.pageSize - Number of voices per page (default: 30)
     * @param {string} options.sort - Sort criteria (e.g., 'name', '-created_at')
     * @param {Object} options.filters - Filter criteria
     * @param {string[]} options.includes - Relations to include
     * @param {boolean} options.append - If true, append results to existing voices
     * @returns {Promise<Object>} Response with data, meta, and links
     */
    const fetchVoices = useCallback(async (options = {}) => {
        if (!company?.id) {
            setVoices([]);
            return;
        }

        const {
            page = 1,
            pageSize = 30,
            sort = '-created_at',
            filters = {},
            includes = ['company', 'audio.thumbnail'],
            append = false
        } = options;

        setLoading(!append);
        setError(null);

        try {
            // Build query parameters
            const queryParams = new URLSearchParams({
                'page[number]': page.toString(),
                'page[size]': pageSize.toString(),
                sort: sort,
                include: includes.join(',')
            });

            // Add filters
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(`filter[${key}]`, value.toString());
                }
            });

            const response = await Api.get(`/companies/${company.id}/voices?${queryParams.toString()}`);
            
            if (append) {
                setVoices(prev => [...prev, ...response.data]);
            } else {
                setVoices(response.data);
            }

            setMeta(response.meta);
            setLinks(response.links);

            return {
                data: response.data,
                meta: response.meta,
                links: response.links
            };
        } catch (err) {
            console.error('Error fetching company voices:', err);
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [company?.id]);

    /**
     * Fetch all company voices (handles pagination automatically)
     * @param {Object} options - Fetch options (same as fetchVoices, except page and append)
     * @returns {Promise<Array>} All voices
     */
    const fetchAllVoices = useCallback(async (options = {}) => {
        if (!company?.id) {
            setVoices([]);
            return [];
        }

        const {
            sort = '-created_at',
            filters = {},
            includes = ['company', 'audio.thumbnail']
        } = options;

        const cacheKey = [
            'voices',
            'legacyAll',
            String(company.id),
            sort,
            JSON.stringify(filters),
            includes.join(','),
        ];

        setLoading(true);
        setError(null);

        try {
            const allVoices = await qc.fetchQuery({
                queryKey: cacheKey,
                staleTime: 60_000,
                queryFn: async () => {
                    const filterQs = Object.entries(filters)
                        .map(
                            ([key, value]) =>
                                `&filter[${key}]=${encodeURIComponent(value)}`
                        )
                        .join('');
                    const initial = await Api.get(
                        `/companies/${company.id}/voices?include=${includes.join(',')}&sort=${sort}&page[size]=30${filterQs}`
                    );
                    let acc = [...initial.data];
                    const totalPages = Math.ceil(
                        initial.meta.total / initial.meta.per_page
                    );
                    if (totalPages > 1) {
                        const remainingCalls = [];
                        for (let page = 2; page <= totalPages; page++) {
                            remainingCalls.push(
                                Api.get(
                                    `/companies/${company.id}/voices?include=${includes.join(',')}&sort=${sort}&page[number]=${page}&page[size]=30${filterQs}`
                                )
                            );
                        }
                        const responses = await Promise.all(remainingCalls);
                        responses.forEach((r) => {
                            acc = [...acc, ...r.data];
                        });
                    }
                    return acc;
                },
            });
            setVoices(allVoices);
            return allVoices;
        } catch (err) {
            console.error('Error fetching all company voices:', err);
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [company?.id, qc]);

    /**
     * Fetch a specific voice by ID
     * @param {string|number} voiceId - The voice ID
     * @param {string[]} includes - Relations to include
     * @returns {Promise<Object>} Voice data
     */
    const fetchVoice = useCallback(async (voiceId, includes = ['company', 'audio.thumbnail']) => {
        setOperationLoadingState(`fetch-${voiceId}`, true);
        setError(null);

        try {
            const response = await Api.get(`/voices/${voiceId}?include=${includes.join(',')}`);
            return response.data;
        } catch (err) {
            console.error('Error fetching voice:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`fetch-${voiceId}`, false);
        }
    }, [setOperationLoadingState]);

    /**
     * Upload voice audio from a file
     * @param {File} file - The audio file to upload
     * @param {string} voiceId - The voice ID to attach the audio to
     * @returns {Promise<Object>} Updated voice with new audio
     */
    const uploadVoiceAudio = useCallback(async (file, voiceId) => {
        setOperationLoadingState(`upload-audio-${voiceId}`, true);
        setError(null);

        try {
            const type = file.type.includes("audio") ? "audio" : "video";

            // Get presigned URL
            const response = await Api.post(
                `/voices/${voiceId}/audio`,
                {},
                {
                    filename: file.name,
                    type: type,
                    visibility: "public"
                }
            );

            const awsUri = response.data.presigned_url;

            // Upload to AWS
            await axios.put(awsUri, file, {
                headers: {
                    'Content-Type': file.type,
                    'x-ms-blob-type': 'BlockBlob'
                }
            });

            // Refetch the voice to get updated audio URL
            const updatedVoice = await fetchVoice(voiceId);
            setVoices(prev => prev.map(voice =>
                voice.id === voiceId ? updatedVoice : voice
            ));

            return updatedVoice;
        } catch (err) {
            console.error('Error uploading voice audio:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`upload-audio-${voiceId}`, false);
        }
    }, [fetchVoice, setOperationLoadingState]);

    /**
     * Upload voice audio from a URL
     * @param {string} url - The audio URL to upload
     * @param {string} voiceId - The voice ID to attach the audio to
     * @returns {Promise<Object>} Updated voice with new audio
     */
    const updateVoiceAudioUrl = useCallback(async (url, voiceId) => {
        setOperationLoadingState(`upload-audio-${voiceId}`, true);
        setError(null);

        try {
            // Fetch the audio from URL
            const response = await fetch(url.startsWith('data:audio') ? url : proxyUrl(url));
            const blob = await response.blob();

            // Determine audio type
            const audioType = !blob?.type?.split('/')?.[1]
                ? url?.includes("data:audio/")
                    ? url?.substring("data:audio/"?.length, url.indexOf(";base64"))
                    : url?.split('.')?.pop()?.toLowerCase()
                : blob?.type?.split('/')?.[1];

            const name = `voice-audio.${audioType}`;
            const file = new File([blob], name, { type: `audio/${audioType}` });
            const type = file.type.split("/")[0];

            // Get presigned URL
            const apiResponse = await Api.post(
                `/voices/${voiceId}/audio`,
                {},
                {
                    filename: name,
                    type: type,
                    visibility: "public"
                }
            );

            const awsUri = apiResponse.data.presigned_url;

            // Upload to AWS
            await axios.put(awsUri, file, {
                headers: {
                    'Content-Type': file.type,
                    'x-ms-blob-type': 'BlockBlob'
                }
            });

            // Refetch the voice to get updated audio URL
            const updatedVoice = await fetchVoice(voiceId);
            setVoices(prev => prev.map(voice =>
                voice.id === voiceId ? updatedVoice : voice
            ));

            return updatedVoice;
        } catch (err) {
            console.error('Error updating voice audio from URL:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`upload-audio-${voiceId}`, false);
        }
    }, [fetchVoice, setOperationLoadingState]);

    /**
     * Create a new voice for the company with optional audio upload
     * @param {Object} voiceData - Voice data
     * @param {string} voiceData.name - Voice name (required)
     * @param {string} voiceData.language_code - Language code (required, e.g., 'en-US', 'es-ES')
     * @param {string} voiceData.accent - Voice accent (optional)
     * @param {string} voiceData.platform - Voice platform (default: 'elevenlabs')
     * @param {File} voiceData.audioFile - Optional audio file to upload
     * @param {string} voiceData.audioUrl - Optional audio URL to use
     * @returns {Promise<Object>} Created voice
     */
    const createVoice = useCallback(async (voiceData) => {
        if (!company?.id) {
            throw new Error('Company ID is required to create voice');
        }

        setOperationLoadingState('create', true);
        setError(null);

        try {
            // Validate required fields
            if (!voiceData.name) {
                throw new Error('Voice name is required');
            }
            if (!voiceData.language_code) {
                throw new Error('Language code is required');
            }

            const payload = {
                name: voiceData.name,
                description: voiceData.description || null,
                language_code: voiceData.language_code,
                accent: voiceData.accent || null,
                platform: voiceData.platform || 'elevenlabs',
                platform_external_id: voiceData.platform_external_id || null
            };

            const response = await Api.post(`/companies/${company.id}/voices`, {}, payload);
            let newVoice = response.data;

            // Handle audio upload if provided
            if (voiceData.audioUrl) {
                // Use URL
                newVoice = await updateVoiceAudioUrl(voiceData.audioUrl, newVoice.id);
            } else if (voiceData.audioFile) {
                // Upload file
                newVoice = await uploadVoiceAudio(voiceData.audioFile, newVoice.id);
            }

            // Update local state - add new voice to the top
            setVoices(prev => [newVoice, ...prev]);
            qc.invalidateQueries({ queryKey: ['voices'] });

            return newVoice;
        } catch (err) {
            console.error('Error creating voice:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState('create', false);
        }
    }, [company?.id, setOperationLoadingState, uploadVoiceAudio, updateVoiceAudioUrl, qc]);

    /**
     * Update an existing voice with optimistic updates
     * @param {string|number} voiceId - The voice ID
     * @param {Object} voiceData - Updated voice data
     * @param {string} voiceData.name - Voice name
     * @param {string} voiceData.language_code - Language code
     * @param {string} voiceData.accent - Voice accent
     * @param {string} voiceData.platform - Voice platform
     * @returns {Promise<Object>} Updated voice
     */
    const updateVoice = useCallback(async (voiceId, voiceData) => {
        setOperationLoadingState(`update-${voiceId}`, true);
        setError(null);

        // Optimistic update: immediately update local state
        const previousVoices = voices;
        const previousVoice = voices.find(voice => voice.id === voiceId) || {};
        
        setVoices(prev => prev.map(voice => 
            voice.id === voiceId ? { ...voice, ...voiceData } : voice
        ));

        try {
            const payload = {};
            if (voiceData.name !== undefined) payload.name = voiceData.name;
            if (voiceData.description !== undefined) payload.description = voiceData.description;
            if (voiceData.language_code !== undefined) payload.language_code = voiceData.language_code;
            if (voiceData.accent !== undefined) payload.accent = voiceData.accent;
            if (voiceData.platform !== undefined) payload.platform = voiceData.platform;
            if (voiceData.platform_external_id !== undefined) payload.platform_external_id = voiceData.platform_external_id;

            const response = await Api.put(`/voices/${voiceId}`, {}, payload);
            const updatedVoice = {
                ...previousVoice,
                ...response.data,
            };

            // Update with actual backend data
            setVoices(prev => prev.map(voice =>
                voice.id === voiceId ? updatedVoice : voice
            ));
            qc.invalidateQueries({ queryKey: ['voices'] });

            return updatedVoice;
        } catch (err) {
            console.error('Error updating voice:', err);
            // Rollback optimistic update on error
            setVoices(previousVoices);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`update-${voiceId}`, false);
        }
    }, [voices, setOperationLoadingState, qc]);

    /**
     * Delete a voice
     * @param {string|number} voiceId - The voice ID
     * @returns {Promise<boolean>} Success status
     */
    const deleteVoice = useCallback(async (voiceId) => {
        setOperationLoadingState(`delete-${voiceId}`, true);
        setError(null);

        try {
            await Api.delete(`/voices/${voiceId}`);

            // Update local state
            setVoices(prev => prev.filter(voice => voice.id !== voiceId));
            qc.invalidateQueries({ queryKey: ['voices'] });

            return true;
        } catch (err) {
            console.error('Error deleting voice:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`delete-${voiceId}`, false);
        }
    }, [setOperationLoadingState, qc]);

    /**
     * Check if an operation is currently loading
     * @param {string} operation - Operation identifier
     * @returns {boolean} Loading status
     */
    const isOperationLoading = useCallback((operation) => {
        return Boolean(operationLoading[operation]);
    }, [operationLoading]);

    /**
     * Fetch ElevenLabs voices with filtering and pagination support
     * @param {Object} options - Fetch options
     * @param {number} options.page - Page number (1-based for UI, converted to 0-based for API)
     * @param {number} options.pageSize - Number of voices per page (default: 30, max: 100)
     * @param {string} options.category - Voice category ('professional', 'famous', 'high_quality')
     * @param {string} options.gender - Gender filter
     * @param {string} options.age - Age filter
     * @param {string} options.accent - Accent filter
     * @param {string} options.language - Language filter
     * @param {string} options.locale - Locale filter
     * @param {string} options.search - Search term
     * @param {string[]} options.useCases - Use case filters
     * @param {string[]} options.descriptives - Descriptive term filters
     * @param {boolean} options.featured - Filter featured voices
     * @param {number} options.minNoticePeriodDays - Minimum notice period filter
     * @param {boolean} options.includeCustomRates - Include voices with custom rates
     * @param {boolean} options.includeLiveModerated - Include live moderated voices
     * @param {boolean} options.readerAppEnabled - Filter voices enabled for reader app
     * @param {string} options.ownerId - Filter by owner ID
     * @param {string} options.sort - Sort criteria
     * @param {boolean} options.append - If true, append results to existing voices (for infinite scroll)
     * @returns {Promise<Object>} Response with data, meta, and links
     */
    const fetchElevenLabsVoices = useCallback(async (options = {}) => {
        const {
            page = 1,
            pageSize = 30,
            category,
            gender,
            age,
            accent,
            language,
            locale,
            search,
            useCases,
            descriptives,
            featured,
            minNoticePeriodDays,
            includeCustomRates,
            includeLiveModerated,
            readerAppEnabled,
            ownerId,
            sort,
            append = false
        } = options;

        setLoading(!append);
        setError(null);

        try {
            // Build query parameters
            const queryParams = new URLSearchParams();
            
            // Convert 1-based page to 0-based for API
            queryParams.append('page', (page - 1).toString());
            queryParams.append('page_size', pageSize.toString());

            // Add optional filters
            if (category) queryParams.append('category', category);
            if (gender) queryParams.append('gender', gender);
            if (age) queryParams.append('age', age);
            if (accent) queryParams.append('accent', accent);
            if (language) queryParams.append('language', language);
            if (locale) queryParams.append('locale', locale);
            if (search) queryParams.append('search', search);
            if (sort) queryParams.append('sort', sort);
            if (ownerId) queryParams.append('owner_id', ownerId);
            
            // Handle array parameters
            if (useCases && Array.isArray(useCases)) {
                useCases.forEach(useCase => {
                    queryParams.append('use_cases[]', useCase);
                });
            }
            
            if (descriptives && Array.isArray(descriptives)) {
                descriptives.forEach(descriptive => {
                    queryParams.append('descriptives[]', descriptive);
                });
            }

            // Handle boolean parameters
            if (featured !== undefined && featured !== null) {
                queryParams.append('featured', featured.toString());
            }
            if (includeCustomRates !== undefined && includeCustomRates !== null) {
                queryParams.append('include_custom_rates', includeCustomRates.toString());
            }
            if (includeLiveModerated !== undefined && includeLiveModerated !== null) {
                queryParams.append('include_live_moderated', includeLiveModerated.toString());
            }
            if (readerAppEnabled !== undefined && readerAppEnabled !== null) {
                queryParams.append('reader_app_enabled', readerAppEnabled.toString());
            }
            
            // Handle numeric parameters
            if (minNoticePeriodDays !== undefined && minNoticePeriodDays !== null) {
                queryParams.append('min_notice_period_days', minNoticePeriodDays.toString());
            }

            const response = await Api.get(`/voices/elevenlabs?${queryParams.toString()}`);
            
            // Update state
            if (append) {
                setVoices(prev => [...prev, ...response.data]);
            } else {
                setVoices(response.data);
            }
            
            setMeta(response.meta);
            setLinks(response.links);

            return {
                data: response.data,
                meta: response.meta,
                links: response.links
            };
        } catch (err) {
            console.error('Error fetching ElevenLabs voices:', err);
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Fetch all ElevenLabs voices (handles pagination automatically)
     * Warning: This may take a while and consume significant resources for large voice libraries
     * @param {Object} options - Fetch options (same as fetchElevenLabsVoices, except page and append)
     * @returns {Promise<Array>} All voices
     */
    const fetchAllElevenLabsVoices = useCallback(async (options = {}) => {
        setLoading(true);
        setError(null);
        let allVoices = [];
        let currentPage = 1;
        let hasMore = true;

        try {
            while (hasMore) {
                const response = await Api.get(
                    `/voices/elevenlabs?${buildQueryString({ ...options, page: currentPage - 1 })}`
                );
                
                allVoices = [...allVoices, ...response.data];
                hasMore = response.meta?.has_more || false;
                currentPage++;

                // Safety check to prevent infinite loops
                if (currentPage > 100) {
                    console.warn('Reached maximum page limit (100) when fetching all voices');
                    break;
                }
            }

            setVoices(allVoices);
            setMeta({
                current_page: 1,
                per_page: allVoices.length,
                has_more: false,
                total: allVoices.length
            });

            return allVoices;
        } catch (err) {
            console.error('Error fetching all ElevenLabs voices:', err);
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Helper function to build query string from options
     * @private
     */
    const buildQueryString = useCallback((options) => {
        const {
            page = 0,
            pageSize = 30,
            category,
            gender,
            age,
            accent,
            language,
            locale,
            search,
            useCases,
            descriptives,
            featured,
            minNoticePeriodDays,
            includeCustomRates,
            includeLiveModerated,
            readerAppEnabled,
            ownerId,
            sort
        } = options;

        const queryParams = new URLSearchParams();
        
        queryParams.append('page', page.toString());
        queryParams.append('page_size', pageSize.toString());

        if (category) queryParams.append('category', category);
        if (gender) queryParams.append('gender', gender);
        if (age) queryParams.append('age', age);
        if (accent) queryParams.append('accent', accent);
        if (language) queryParams.append('language', language);
        if (locale) queryParams.append('locale', locale);
        if (search) queryParams.append('search', search);
        if (sort) queryParams.append('sort', sort);
        if (ownerId) queryParams.append('owner_id', ownerId);
        
        if (useCases && Array.isArray(useCases)) {
            useCases.forEach(useCase => {
                queryParams.append('use_cases[]', useCase);
            });
        }
        
        if (descriptives && Array.isArray(descriptives)) {
            descriptives.forEach(descriptive => {
                queryParams.append('descriptives[]', descriptive);
            });
        }

        if (featured !== undefined && featured !== null) {
            queryParams.append('featured', featured.toString());
        }
        if (includeCustomRates !== undefined && includeCustomRates !== null) {
            queryParams.append('include_custom_rates', includeCustomRates.toString());
        }
        if (includeLiveModerated !== undefined && includeLiveModerated !== null) {
            queryParams.append('include_live_moderated', includeLiveModerated.toString());
        }
        if (readerAppEnabled !== undefined && readerAppEnabled !== null) {
            queryParams.append('reader_app_enabled', readerAppEnabled.toString());
        }
        
        if (minNoticePeriodDays !== undefined && minNoticePeriodDays !== null) {
            queryParams.append('min_notice_period_days', minNoticePeriodDays.toString());
        }

        return queryParams.toString();
    }, []);

    /**
     * Get voices filtered by local criteria (filters already fetched voices)
     * @param {Object} filters - Filter criteria
     * @param {string} filters.name - Filter by name (case-insensitive partial match)
     * @param {string} filters.gender - Filter by gender
     * @param {string} filters.language - Filter by language
     * @param {string} filters.accent - Filter by accent
     * @param {string} filters.category - Filter by category
     * @param {boolean} filters.featured - Filter by featured status
     * @param {boolean} filters.freeUsersAllowed - Filter by free users allowed status
     * @returns {Array} Filtered voices
     */
    const getFilteredVoices = useCallback((filters = {}) => {
        return voices.filter(voice => {
            if (filters.name && !voice.name.toLowerCase().includes(filters.name.toLowerCase())) {
                return false;
            }
            if (filters.gender && voice.gender !== filters.gender) {
                return false;
            }
            if (filters.language && voice.language !== filters.language) {
                return false;
            }
            if (filters.accent && voice.accent !== filters.accent) {
                return false;
            }
            if (filters.category && voice.category !== filters.category) {
                return false;
            }
            if (filters.featured !== undefined && voice.featured !== filters.featured) {
                return false;
            }
            if (filters.freeUsersAllowed !== undefined && voice.free_users_allowed !== filters.freeUsersAllowed) {
                return false;
            }
            return true;
        });
    }, [voices]);

    /**
     * Get unique values for a specific voice attribute (useful for building filter dropdowns)
     * @param {string} attribute - The attribute name (e.g., 'gender', 'language', 'accent', 'category')
     * @returns {Array} Sorted unique values
     */
    const getUniqueAttributeValues = useCallback((attribute) => {
        const values = voices
            .map(voice => voice[attribute])
            .filter(value => value !== null && value !== undefined && value !== '');
        
        return [...new Set(values)].sort();
    }, [voices]);

    /**
     * Search voices by name or description
     * @param {string} searchTerm - Search term
     * @returns {Array} Matching voices
     */
    const searchVoices = useCallback((searchTerm) => {
        if (!searchTerm) return voices;
        
        const lowerSearchTerm = searchTerm.toLowerCase();
        return voices.filter(voice => 
            voice.name?.toLowerCase().includes(lowerSearchTerm) ||
            voice.description?.toLowerCase().includes(lowerSearchTerm) ||
            voice.descriptive?.toLowerCase().includes(lowerSearchTerm)
        );
    }, [voices]);

    return {
        // State
        voices,
        loading,
        error,
        meta,
        links,
        
        // Company Voice Operations
        fetchVoices,
        fetchAllVoices,
        fetchVoice,
        createVoice,
        updateVoice,
        deleteVoice,
        uploadVoiceAudio,
        updateVoiceAudioUrl,
        
        // ElevenLabs Operations
        fetchElevenLabsVoices,
        fetchAllElevenLabsVoices,
        
        // Utilities
        getFilteredVoices,
        getUniqueAttributeValues,
        searchVoices,
        clearError,
        isOperationLoading,
        
        // Computed values
        voiceCount: voices.length,
        hasVoices: voices.length > 0,
        hasMore: meta?.has_more || false,
        currentPage: meta?.current_page || 1,
        totalVoices: meta?.total || 0,
    };
};

export default useVoices;

